import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { DateRangePicker, DollarFormatter, Page, PercentageFormatter } from '@src/components';
import { fetchAdSummaryByAd } from '@src/api';
import api from '@src/api/clients';
import paths from '@src/paths';
import { formatDateToYMD } from '@src/utils/dateUtils';

import {
  CLIENT_FIELDS,
  MAX_CLIENT_META_PAGES,
  arraysEqual,
  buildPeerIndex,
  computeDerivedMetrics,
  formatPercentLabel,
  gateRecommendationByComConfidence,
  getBenchmarkDelta,
  getConfidenceScore,
  getDecisionCostMetrics,
  getFunnelBenchmarks,
  getLastThirtyDays,
  getMaturityLabel,
  getPeersFromIndex,
  getPrimaryKpi,
  getRatioTone,
  getRecommendation,
  getRecommendationTone,
  getSpendShareFromIndex,
  getStatusPillStyles,
  hasUsableBenchmarks,
  inferCreativeLabels,
  normalizeCampaignType,
  safeNumber,
} from '../creative-performance/utils';

const AD_NAME_FILTER = 'buzz';
const MIN_PEERS_FOR_COMPARISON = 3;
const COMPARISON_NEUTRAL_BAND = 0.05;

// Set to false before shipping — local preview only.
const USE_MOCK_DATA = false;

const MOCK_CLIENT_METADATA = [
  { id: '101', name: 'Acme Bath Co', parentCompanyId: '1', parentCompanyName: 'Five Star Bath Solutions', hasMapping: true },
  { id: '102', name: 'Bright Smile Dental', parentCompanyId: '__none__', parentCompanyName: 'No Parent Company', hasMapping: true },
  { id: '103', name: 'Cozy Roofing', parentCompanyId: '__none__', parentCompanyName: 'No Parent Company', hasMapping: true },
];

const buildMockAdRow = ({
  adId, adName, clientId, clientName, headline, primaryText, thumbnail,
  spend, impressions, clicks, lpv, videoPlays3s, thruplays,
  leads, booked, demos, netSold, netAmount, active = true,
  flightId, flightCode, taskId, taskCode,
}) => ({
  uuid: adId,
  ad_id: adId,
  ad_name: adName,
  ad_set_name: `${adName} — Ad Set`,
  campaign: `${clientName} — Buzz Reels`,
  client_id: clientId,
  client_name: clientName,
  parent_company_id: clientId === '101' ? 1 : null,
  active,
  vertical: 'Home Services',
  campaign_type: 'Broad',
  headline,
  primary_text: primaryText,
  description: 'Limited time offer.',
  destination_url: 'https://example.com/landing',
  cta_type: 'LEARN_MORE',
  thumbnail_url: thumbnail,
  image_url: thumbnail,
  client_count: 1,
  backend_client_count: 1,
  deal_value_stability: 'Stable',
  deal_value_stability_dampener: 1,
  deal_value_cv: 0.2,
  total_spend_sum: spend,
  impressions_sum: impressions,
  clicks_sum: clicks,
  landing_page_views_sum: lpv,
  video_plays_3s_sum: videoPlays3s,
  thruplays_sum: thruplays,
  total_leads_sum: leads,
  self_books_sum: 0,
  apps_booked_sum: booked,
  scheduled_sum: booked,
  issue_sum: Math.round(booked * 0.7),
  demo_sum: demos,
  gross_sales_sum: netSold,
  net_sales_sum: netSold,
  net_amount_sum: netAmount,
  mature_spend_sum: spend,
  mature_leads_sum: leads,
  mature_self_books_sum: 0,
  mature_booked_sum: booked,
  mature_scheduled_sum: booked,
  mature_issues_sum: Math.round(booked * 0.7),
  mature_demos_sum: demos,
  mature_gross_sales_sum: netSold,
  mature_net_sales_sum: netSold,
  mature_net_amount_sum: netAmount,
  flight_id: flightId || null,
  flight_code: flightCode || null,
  task_id: taskId || null,
  task_code: taskCode || null,
});

const MOCK_AD_ROWS = [
  // Acme Bath — strong winner, mid, underperformer
  buildMockAdRow({
    adId: 'buzz-acme-1', adName: 'Buzz Reel — Tub Transformation', clientId: '101', clientName: 'Acme Bath Co',
    headline: 'New Tub in a Day', primaryText: 'See how families upgraded in under 24 hours.',
    thumbnail: 'https://picsum.photos/seed/buzz1/400/400',
    spend: 2400, impressions: 180000, clicks: 4800, lpv: 4100,
    videoPlays3s: 95000, thruplays: 18000, leads: 220, booked: 110, demos: 70, netSold: 22, netAmount: 198000,
    flightId: 8, flightCode: 'FLT-0008', taskId: 83, taskCode: 'FLT-0008-T0083',
  }),
  buildMockAdRow({
    adId: 'buzz-acme-2', adName: 'Buzz Reel — Walk-In Shower', clientId: '101', clientName: 'Acme Bath Co',
    headline: 'Safer Showers, No Demo', primaryText: 'Slip-resistant walk-in shower in 1 day.',
    thumbnail: 'https://picsum.photos/seed/buzz2/400/400',
    spend: 1800, impressions: 130000, clicks: 2200, lpv: 1700,
    videoPlays3s: 52000, thruplays: 7000, leads: 95, booked: 40, demos: 22, netSold: 8, netAmount: 64000,
    flightId: 8, flightCode: 'FLT-0008', taskId: 84, taskCode: 'FLT-0008-T0084',
  }),
  buildMockAdRow({
    adId: 'buzz-acme-3', adName: 'Buzz Reel — Customer Stories', clientId: '101', clientName: 'Acme Bath Co',
    headline: 'Real Customer Reviews', primaryText: 'Hear from homeowners who chose Acme.',
    thumbnail: 'https://picsum.photos/seed/buzz3/400/400',
    spend: 950, impressions: 60000, clicks: 600, lpv: 320,
    videoPlays3s: 14000, thruplays: 1100, leads: 18, booked: 6, demos: 3, netSold: 1, netAmount: 7200,
  }),
  // Bright Smile Dental — strong winner, mid, underperformer
  buildMockAdRow({
    adId: 'buzz-bs-1', adName: 'Buzz Reel — Same Day Veneers', clientId: '102', clientName: 'Bright Smile Dental',
    headline: 'Veneers in One Visit', primaryText: 'Walk in, walk out with a brand new smile.',
    thumbnail: 'https://picsum.photos/seed/buzz4/400/400',
    spend: 3100, impressions: 210000, clicks: 6300, lpv: 5400,
    videoPlays3s: 120000, thruplays: 24000, leads: 310, booked: 180, demos: 130, netSold: 42, netAmount: 168000,
    flightId: 11, flightCode: 'FLT-0011', taskId: 102, taskCode: 'FLT-0011-T0102',
  }),
  buildMockAdRow({
    adId: 'buzz-bs-2', adName: 'Buzz Reel — Free Consultation', clientId: '102', clientName: 'Bright Smile Dental',
    headline: 'Free Smile Consult', primaryText: 'Book your no-cost consultation today.',
    thumbnail: 'https://picsum.photos/seed/buzz5/400/400',
    spend: 1400, impressions: 95000, clicks: 1700, lpv: 1300,
    videoPlays3s: 38000, thruplays: 5400, leads: 70, booked: 32, demos: 18, netSold: 7, netAmount: 26000,
  }),
  buildMockAdRow({
    adId: 'buzz-bs-3', adName: 'Buzz Reel — Before & After', clientId: '102', clientName: 'Bright Smile Dental',
    headline: 'See the Transformation', primaryText: 'Real before-and-after smile makeovers.',
    thumbnail: 'https://picsum.photos/seed/buzz6/400/400',
    spend: 700, impressions: 42000, clicks: 380, lpv: 220,
    videoPlays3s: 9500, thruplays: 800, leads: 12, booked: 4, demos: 2, netSold: 0, netAmount: 0,
    active: false,
  }),
  // Cozy Roofing — only 2 ads (will not have comparison color — useful test of the gate)
  buildMockAdRow({
    adId: 'buzz-cz-1', adName: 'Buzz Reel — Storm-Ready Roof', clientId: '103', clientName: 'Cozy Roofing',
    headline: 'Storm Season is Coming', primaryText: 'Inspect your roof before the next storm.',
    thumbnail: 'https://picsum.photos/seed/buzz7/400/400',
    spend: 1200, impressions: 80000, clicks: 1500, lpv: 1100,
    videoPlays3s: 32000, thruplays: 4200, leads: 55, booked: 22, demos: 12, netSold: 4, netAmount: 36000,
    flightId: 14, flightCode: 'FLT-0014', taskId: 121, taskCode: 'FLT-0014-T0121',
  }),
  buildMockAdRow({
    adId: 'buzz-cz-2', adName: 'Buzz Reel — Free Roof Inspection', clientId: '103', clientName: 'Cozy Roofing',
    headline: 'Free Roof Check', primaryText: 'Same-week roof inspection at no cost.',
    thumbnail: 'https://picsum.photos/seed/buzz8/400/400',
    spend: 600, impressions: 38000, clicks: 540, lpv: 380,
    videoPlays3s: 12000, thruplays: 1600, leads: 18, booked: 7, demos: 3, netSold: 1, netAmount: 8500,
  }),
];

const COMPARISON_METRICS = [
  { key: 'hookRate', label: 'Hook Rate', better: 'higher', requires: (row) => row.videoPlays3s > 0 },
  { key: 'ctr', label: 'CTR', better: 'higher', requires: (row) => row.impressions > 0 },
  { key: 'lpvRate', label: 'LPV Rate', better: 'higher', requires: (row) => row.clicks > 0 },
  { key: 'thruplayRate', label: 'Thruplay Rate', better: 'higher', requires: (row) => row.videoPlays3s > 0 },
  { key: 'cpl', label: 'CPL', better: 'lower', requires: (row) => row.leads > 0 },
];

const computeClientAverages = (rows) => {
  const byClient = new Map();
  rows.forEach((row) => {
    if (!row.clientId) return;
    if (!byClient.has(row.clientId)) {
      byClient.set(row.clientId, { count: 0, spend: 0, impressions: 0, clicks: 0, landingPageViews: 0, videoPlays3s: 0, thruplays: 0, leads: 0 });
    }
    const agg = byClient.get(row.clientId);
    agg.count += 1;
    agg.spend += row.spend;
    agg.impressions += row.impressions;
    agg.clicks += row.clicks;
    agg.landingPageViews += row.landingPageViews;
    agg.videoPlays3s += row.videoPlays3s;
    agg.thruplays += row.thruplays;
    agg.leads += row.leads;
  });
  const result = new Map();
  byClient.forEach((agg, clientId) => {
    result.set(clientId, { ...computeDerivedMetrics(agg), sampleSize: agg.count });
  });
  return result;
};

const getComparison = (row, clientAvgs, metric) => {
  const avg = clientAvgs.get(row.clientId);
  if (!avg || avg.sampleSize < MIN_PEERS_FOR_COMPARISON) return null;
  if (!metric.requires(row)) return null;
  const avgValue = avg[metric.key];
  const rowValue = row[metric.key];
  if (!avgValue || avgValue <= 0) return null;
  const delta = (rowValue - avgValue) / avgValue;
  const isBetter = metric.better === 'higher' ? delta > 0 : delta < 0;
  return { delta, avgValue, rowValue, isBetter, sampleSize: avg.sampleSize };
};

const formatComparisonDelta = (comp) => {
  if (!comp) return null;
  const pct = comp.delta * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(0)}% vs avg`;
};

async function fetchAllClientMetadata() {
  const allClients = [];
  let page = 1;
  while (page <= MAX_CLIENT_META_PAGES) {
    const response = await api.request({
      url: 'dashboard/clients/',
      method: 'GET',
      payload: { page, page_size: 250, ordering: 'name', fields: CLIENT_FIELDS },
    });
    const data = response?.data;
    if (Array.isArray(data)) { allClients.push(...data); break; }
    const results = Array.isArray(data?.results) ? data.results : [];
    allClients.push(...results);
    if (!data?.next) break;
    page += 1;
  }
  return allClients;
}

const mapRow = (row = {}, clientMetaById) => {
  const creativeLabels = inferCreativeLabels(row);
  const clientId = row.client_id ? String(row.client_id) : '';
  const clientMeta = clientId ? clientMetaById.get(clientId) : null;
  const backendClientCount = safeNumber(row.backend_client_count);
  const hasBackendIntegration = Boolean(clientMeta?.hasMapping) || backendClientCount > 0;
  const base = {
    uuid: row.uuid || `${row.ad_id || row.ad_name || ''}-${clientId}`,
    clientId,
    clientName: clientMeta?.name || row.client_name || row.client__name || '',
    parentCompanyId: clientMeta?.parentCompanyId || (row.parent_company_id ? String(row.parent_company_id) : '__none__'),
    adId: row.ad_id || '',
    adName: row.ad_name || row.ad || '',
    adSetName: row.ad_set_name || '',
    campaign: row.campaign || '',
    isActive: row.active == null ? true : Boolean(row.active),
    headline: row.headline || '',
    primaryText: row.primary_text || '',
    description: row.description || '',
    destinationUrl: row.destination_url || '',
    ctaType: row.cta_type || '',
    thumbnailUrl: row.thumbnail_url || '',
    imageUrl: row.image_url || '',
    facebookFormat: creativeLabels.facebookFormat,
    adType: creativeLabels.adType,
    verticalName: row.vertical || '',
    campaignType: normalizeCampaignType(row.campaign_type || ''),
    hasBackendIntegration,
    backendClientCount,
    clientCount: safeNumber(row.client_count),
    dealValueCv: row.deal_value_cv == null ? null : safeNumber(row.deal_value_cv),
    dealValueStability: row.deal_value_stability || 'Unknown',
    dealValueStabilityDampener: safeNumber(row.deal_value_stability_dampener) || 0.8,
    spend: safeNumber(row.total_spend_sum),
    impressions: safeNumber(row.impressions_sum),
    clicks: safeNumber(row.clicks_sum),
    landingPageViews: safeNumber(row.landing_page_views_sum),
    videoPlays3s: safeNumber(row.video_plays_3s_sum),
    thruplays: safeNumber(row.thruplays_sum),
    leads: safeNumber(row.total_leads_sum),
    selfBook: safeNumber(row.self_books_sum),
    booked: safeNumber(row.apps_booked_sum || row.booked_sum),
    scheduled: safeNumber(row.scheduled_sum),
    issues: safeNumber(row.issue_sum || row.issues_sum),
    demos: safeNumber(row.demo_sum || row.demos_sum),
    grossSold: safeNumber(row.gross_sold_sum || row.gross_sales_sum),
    netSold: safeNumber(row.net_sold_sum || row.net_sales_sum),
    netAmount: safeNumber(row.net_total_sum || row.net_amount_sum),
    matureSpend: safeNumber(row.mature_spend_sum),
    matureLeads: safeNumber(row.mature_leads_sum),
    matureSelfBook: safeNumber(row.mature_self_books_sum),
    matureBooked: safeNumber(row.mature_booked_sum),
    matureScheduled: safeNumber(row.mature_scheduled_sum),
    matureIssues: safeNumber(row.mature_issues_sum),
    matureDemos: safeNumber(row.mature_demos_sum),
    matureGrossSold: safeNumber(row.mature_gross_sales_sum),
    matureNetSold: safeNumber(row.mature_net_sales_sum),
    matureNetAmount: safeNumber(row.mature_net_amount_sum),
    flightId: row.flight_id || null,
    flightCode: row.flight_code || '',
    taskId: row.task_id || null,
    taskCode: row.task_code || '',
  };
  const merged = { ...base, ...computeDerivedMetrics(base) };
  return { ...merged, maturityLabel: getMaturityLabel(merged) };
};

const scoreRow = (row, peerIndex) => {
  const primaryKpi = getPrimaryKpi(row);
  const clientPeers = getPeersFromIndex(row, peerIndex, 'client');
  const agencyPeers = getPeersFromIndex(row, peerIndex, 'agency');
  const backendClientPeers = clientPeers.filter((p) => p.hasBackendIntegration);
  const backendAgencyPeers = agencyPeers.filter((p) => p.hasBackendIntegration);

  const clientBenchmarks = getFunnelBenchmarks(backendClientPeers);
  const agencyBenchmarks = getFunnelBenchmarks(backendAgencyPeers);
  const selectedBenchmarks = hasUsableBenchmarks(clientBenchmarks) ? clientBenchmarks : agencyBenchmarks;

  const decisionCostMetrics = getDecisionCostMetrics(row, selectedBenchmarks);
  const { predictedCostOfMarketing, effectiveCostOfMarketing, comConfidenceBand, comConfidenceLabel, dealValueStability } = decisionCostMetrics;

  const decisionCostKpi = effectiveCostOfMarketing > 0
    ? {
      key: 'effectiveCostOfMarketing',
      label: decisionCostMetrics.decisionCostLabel,
      better: 'lower',
      benchmarkScope: 'client_vertical',
      isEligible: (c) => safeNumber(c.effectiveCostOfMarketing) > 0,
    }
    : primaryKpi;

  const enrichedClientPeers = clientPeers.map((p) => ({ ...p, ...getDecisionCostMetrics(p, selectedBenchmarks) }));
  const enrichedAgencyPeers = agencyPeers.map((p) => ({ ...p, ...getDecisionCostMetrics(p, selectedBenchmarks) }));
  const enrichedRow = { ...row, effectiveCostOfMarketing };

  const clientDelta = getBenchmarkDelta(enrichedRow, enrichedClientPeers, decisionCostKpi);
  const agencyDelta = getBenchmarkDelta(enrichedRow, enrichedAgencyPeers, decisionCostKpi);
  const benchmarkDelta = decisionCostKpi?.benchmarkScope === 'agency_vertical' ? agencyDelta : clientDelta;

  const confidenceScore = getConfidenceScore(row);
  const spendShare = getSpendShareFromIndex(row, peerIndex);

  const baseRecommendation = getRecommendation(enrichedRow, decisionCostKpi, benchmarkDelta);
  const overallRecommendation = gateRecommendationByComConfidence({
    recommendation: baseRecommendation,
    primaryKpi: decisionCostKpi,
    comConfidenceBand,
    dealValueStability,
    confidenceScore,
    whyDrivers: [],
  });

  return {
    predictedCostOfMarketing,
    effectiveCostOfMarketing,
    comConfidenceLabel,
    spendShare,
    overallRecommendation,
  };
};

function StatusPill({ label, tone = 'neutral' }) {
  const colors = getStatusPillStyles(tone);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1.1,
        py: 0.45,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        backgroundColor: colors.bg,
        color: colors.fg,
      }}
    >
      {label || '-'}
    </Box>
  );
}

function ComparisonNote({ comp, compact = false }) {
  if (!comp) return null;
  const pct = comp.delta * 100;
  const sign = pct > 0 ? '+' : '';
  const label = compact
    ? `${sign}${pct.toFixed(0)}%`
    : `${sign}${pct.toFixed(0)}% vs avg`;
  return (
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        fontWeight: 600,
        fontSize: compact ? 10 : undefined,
        lineHeight: compact ? 1.2 : undefined,
        color: comp.isBetter ? 'success.main' : 'error.main',
      }}
    >
      {label}
    </Typography>
  );
}

function DesignerPreviewDialog({ row, onClose }) {
  if (!row) return null;
  const previewSrc = row.imageUrl || row.thumbnailUrl;
  const comparisons = row.comparisons || {};
  return (
    <Dialog open={Boolean(row)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{row.adName || 'Ad Preview'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="overline" color="text.secondary">Client</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.clientName || '-'}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="overline" color="text.secondary">Status</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.isActive ? 'Active' : 'Paused'}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="overline" color="text.secondary">Format</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.facebookFormat || row.adType || '-'}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="overline" color="text.secondary">Flight / Task</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {row.taskCode ? (
                  <Link component={RouterLink} to={paths.creativeTasks} underline="hover">
                    {row.taskCode}
                  </Link>
                ) : '—'}
              </Typography>
            </Box>
          </Stack>
          {previewSrc ? (
            <Box
              component="img"
              src={previewSrc}
              alt={row.adName || 'Ad creative preview'}
              sx={{ width: '100%', maxHeight: 360, objectFit: 'contain', borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'grey.100' }}
            />
          ) : null}
          <Box>
            <Typography variant="overline" color="text.secondary">Headline</Typography>
            <Typography variant="body1" sx={{ fontWeight: 700 }}>{row.headline || 'No headline stored.'}</Typography>
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">Primary Text</Typography>
            <Typography variant="body2">{row.primaryText || 'No primary text stored.'}</Typography>
          </Box>
          {row.description ? (
            <Box>
              <Typography variant="overline" color="text.secondary">Description</Typography>
              <Typography variant="body2">{row.description}</Typography>
            </Box>
          ) : null}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="overline" color="text.secondary">Destination</Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{row.destinationUrl || '-'}</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="overline" color="text.secondary">CTA</Typography>
              <Typography variant="body2">{row.ctaType || '-'}</Typography>
            </Box>
          </Stack>
          <Box>
            <Typography variant="overline" color="text.secondary">Delivery</Typography>
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, mt: 0.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Spend</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}><DollarFormatter value={row.spend} /></Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Impressions</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{row.impressions.toLocaleString()}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Hook Rate</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {row.videoPlays3s > 0 ? <PercentageFormatter value={row.hookRate} /> : '-'}
                </Typography>
                <ComparisonNote comp={comparisons.hookRate} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">CTR</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}><PercentageFormatter value={row.ctr} /></Typography>
                <ComparisonNote comp={comparisons.ctr} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">LPV Rate</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {row.clicks > 0 ? <PercentageFormatter value={row.lpvRate} /> : '-'}
                </Typography>
                <ComparisonNote comp={comparisons.lpvRate} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Thruplay Rate</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {row.videoPlays3s > 0 ? <PercentageFormatter value={row.thruplayRate} /> : '-'}
                </Typography>
                <ComparisonNote comp={comparisons.thruplayRate} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Leads</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{row.leads.toLocaleString()}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">CPL</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {row.cpl > 0 ? <DollarFormatter value={row.cpl} /> : '-'}
                </Typography>
                <ComparisonNote comp={comparisons.cpl} />
              </Box>
            </Box>
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">Backend</Typography>
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(7, 1fr)' }, mt: 0.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Booked</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{row.booked.toLocaleString()}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Set Rate</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}><PercentageFormatter value={row.setRate} /></Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Demos</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{row.demos.toLocaleString()}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Sold</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{row.netSold.toLocaleString()}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Revenue</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}><DollarFormatter value={row.netAmount} /></Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Close Rate</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}><PercentageFormatter value={row.netCloseRate} /></Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">CoM</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {row.netAmount > 0 ? <PercentageFormatter value={row.costOfMarketing} /> : '-'}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">Decision</Typography>
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, mt: 0.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Predicted CoM</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {row.predictedCostOfMarketing != null ? formatPercentLabel(row.predictedCostOfMarketing) : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Decision CoM</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {row.effectiveCostOfMarketing != null ? formatPercentLabel(row.effectiveCostOfMarketing) : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Spend Share</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {row.spendShare > 0 ? formatPercentLabel(row.spendShare) : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Recommendation</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{row.overallRecommendation || '-'}</Typography>
              </Box>
            </Box>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

export default function DesignerCreativePerformance() {
  const [defaultStart, defaultEnd] = React.useMemo(() => getLastThirtyDays(), []);
  const [startDate, setStartDate] = React.useState(defaultStart);
  const [endDate, setEndDate] = React.useState(defaultEnd);
  const [loading, setLoading] = React.useState(true);
  const [loadingClientMeta, setLoadingClientMeta] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [clientMetadata, setClientMetadata] = React.useState([]);
  const [selectedClients, setSelectedClients] = React.useState([]);
  const [previewRow, setPreviewRow] = React.useState(null);

  React.useEffect(() => {
    let isMounted = true;
    if (USE_MOCK_DATA) {
      setClientMetadata(MOCK_CLIENT_METADATA);
      setLoadingClientMeta(false);
      return () => { isMounted = false; };
    }
    const loadClients = async () => {
      setLoadingClientMeta(true);
      try {
        const clients = await fetchAllClientMetadata();
        if (!isMounted) return;
        setClientMetadata(clients.map((client = {}) => ({
          id: String(client.id),
          name: client.name || '',
          parentCompanyId: client.parent_company ? String(client.parent_company) : '__none__',
          parentCompanyName: client.parent_company_name || 'No Parent Company',
          hasMapping: Array.isArray(client.mapping_types) && client.mapping_types.length > 0,
        })));
      } catch (err) {
        console.error('Failed to load client metadata:', err);
        if (isMounted) { setClientMetadata([]); setError('Unable to load client metadata.'); }
      } finally {
        if (isMounted) setLoadingClientMeta(false);
      }
    };
    loadClients();
    return () => { isMounted = false; };
  }, []);

  const clientMetaById = React.useMemo(() => {
    const next = new Map();
    clientMetadata.forEach((c) => next.set(c.id, c));
    return next;
  }, [clientMetadata]);

  const clientOptions = React.useMemo(
    () => clientMetadata.map((c) => ({ id: c.id, name: c.name })).sort((a, b) => a.name.localeCompare(b.name)),
    [clientMetadata]
  );

  React.useEffect(() => {
    const allowed = new Set(clientOptions.map((o) => o.id));
    setSelectedClients((prev) => { const next = prev.filter((id) => allowed.has(id)); return arraysEqual(prev, next) ? prev : next; });
  }, [clientOptions]);

  const effectiveClientIds = React.useMemo(() => {
    const scopedClients = selectedClients.length > 0
      ? clientMetadata.filter((c) => selectedClients.includes(c.id))
      : clientMetadata;
    return scopedClients.map((c) => c.id);
  }, [clientMetadata, selectedClients]);

  React.useEffect(() => {
    let isMounted = true;
    if (loadingClientMeta) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        let data;
        if (USE_MOCK_DATA) {
          data = MOCK_AD_ROWS;
        } else {
          const payload = {
            date_after: formatDateToYMD(startDate),
            date_before: formatDateToYMD(endDate),
            ordering: '-total_spend_sum',
            ad_name: AD_NAME_FILTER,
            ...(effectiveClientIds.length ? { client_ids: effectiveClientIds.join(',') } : {}),
          };
          const response = await fetchAdSummaryByAd(payload);
          data = Array.isArray(response?.data) ? response.data : Array.isArray(response?.data?.results) ? response.data.results : [];
        }
        if (!isMounted) return;
        const mapped = data.map((row) => mapRow(row, clientMetaById));
        const peerIndex = buildPeerIndex(mapped);
        const scored = mapped
          .map((row) => ({ ...row, ...scoreRow(row, peerIndex) }))
          .sort((a, b) => b.spend - a.spend);
        setRows(scored);
      } catch (err) {
        console.error('Failed to load designer creative performance:', err);
        if (isMounted) { setRows([]); setError('Unable to load creative performance data.'); }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => { isMounted = false; };
  }, [clientMetaById, effectiveClientIds, endDate, loadingClientMeta, startDate]);

  const totals = React.useMemo(() => {
    const base = rows.reduce((acc, row) => {
      acc.spend += row.spend;
      acc.impressions += row.impressions;
      acc.clicks += row.clicks;
      acc.landingPageViews += row.landingPageViews;
      acc.videoPlays3s += row.videoPlays3s;
      acc.thruplays += row.thruplays;
      acc.leads += row.leads;
      acc.booked += row.booked;
      acc.scheduled += row.scheduled;
      acc.issues += row.issues;
      acc.demos += row.demos;
      acc.netSold += row.netSold;
      acc.netAmount += row.netAmount;
      return acc;
    }, { spend: 0, impressions: 0, clicks: 0, landingPageViews: 0, videoPlays3s: 0, thruplays: 0, leads: 0, booked: 0, scheduled: 0, issues: 0, demos: 0, netSold: 0, netAmount: 0 });
    return { ...base, ...computeDerivedMetrics(base) };
  }, [rows]);

  const clientAvgs = React.useMemo(() => computeClientAverages(rows), [rows]);

  const handleDateRangeChange = (event) => {
    const selection = event?.selection || {};
    if (selection.startDate) setStartDate(selection.startDate);
    if (selection.endDate) setEndDate(selection.endDate);
  };

  return (
    <Page title="Designer Creative Performance" fullWidth>
      <Stack spacing={3}>
        {error && <Alert severity="error">{error}</Alert>}

        <Card>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', lg: 'center' }}>
              <Box sx={{ minWidth: 280, maxWidth: { lg: 320 } }}>
                <DateRangePicker startDate={startDate} endDate={endDate} onChange={handleDateRangeChange} style={{ width: '100%' }} />
              </Box>
              <Autocomplete
                multiple
                size="small"
                sx={{ minWidth: 200, flex: 1 }}
                options={clientOptions.map((o) => ({ label: o.name, value: o.id }))}
                value={clientOptions.filter((o) => selectedClients.includes(o.id)).map((o) => ({ label: o.name, value: o.id }))}
                onChange={(_, v) => setSelectedClients(v.map((o) => o.value))}
                isOptionEqualToValue={(o, v) => o.value === v.value}
                getOptionLabel={(o) => o.label || ''}
                renderInput={(params) => <TextField {...params} label="Clients" />}
              />
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0, flexWrap: 'wrap' }}>
                <Box sx={{ textAlign: 'center', px: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Spend</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}><DollarFormatter value={totals.spend} /></Typography>
                </Box>
                <Box sx={{ textAlign: 'center', px: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Leads</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{totals.leads.toLocaleString()}</Typography>
                </Box>
                <Box sx={{ textAlign: 'center', px: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Booked</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{totals.booked.toLocaleString()}</Typography>
                </Box>
                <Box sx={{ textAlign: 'center', px: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Sold</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{totals.netSold.toLocaleString()}</Typography>
                </Box>
                <Box sx={{ textAlign: 'center', px: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Revenue</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}><DollarFormatter value={totals.netAmount} /></Typography>
                </Box>
                <Box sx={{ textAlign: 'center', px: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">CoM</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    {totals.netAmount > 0 ? <PercentageFormatter value={totals.costOfMarketing} /> : '-'}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center', px: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Ads</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{rows.length}</Typography>
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: 0 }}>
            {loading || loadingClientMeta ? (
              <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
            ) : (
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 88 }}>Preview</TableCell>
                      <TableCell>Ad</TableCell>
                      <TableCell>Flight / Task</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Spend</TableCell>
                      <TableCell align="right">Impressions</TableCell>
                      <TableCell align="right">Hook</TableCell>
                      <TableCell align="right">CTR</TableCell>
                      <TableCell align="right">LPV</TableCell>
                      <TableCell align="right">Thruplay</TableCell>
                      <TableCell align="right">Leads</TableCell>
                      <TableCell align="right">CPL</TableCell>
                      <TableCell align="right">Booked</TableCell>
                      <TableCell align="right">Demos</TableCell>
                      <TableCell align="right">Sold</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                      <TableCell align="right">CoM</TableCell>
                      <TableCell align="right">Pred. CoM</TableCell>
                      <TableCell align="right">Decision CoM</TableCell>
                      <TableCell align="right">Spend Share</TableCell>
                      <TableCell>Recommendation</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={22} align="center" sx={{ py: 6 }}>
                          <Typography variant="body2" color="text.secondary">
                            No ads matching &quot;{AD_NAME_FILTER}&quot; in this range.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : rows.map((row) => {
                      const thumb = row.thumbnailUrl || row.imageUrl;
                      const comparisons = COMPARISON_METRICS.reduce((acc, metric) => {
                        acc[metric.key] = getComparison(row, clientAvgs, metric);
                        return acc;
                      }, {});
                      return (
                        <TableRow
                          key={row.uuid}
                          hover
                          onClick={() => setPreviewRow({ ...row, comparisons })}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>
                            {thumb ? (
                              <Box
                                component="img"
                                src={thumb}
                                alt={row.adName}
                                sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'grey.100' }}
                              />
                            ) : (
                              <Box sx={{ width: 72, height: 72, borderRadius: 1, border: '1px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography variant="caption" color="text.secondary">No image</Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 320 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {row.adName || '(no name)'}
                            </Typography>
                            {row.headline ? (
                              <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                                {row.headline}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            {row.taskCode ? (
                              <Link
                                component={RouterLink}
                                to={paths.creativeTasks}
                                onClick={(e) => e.stopPropagation()}
                                underline="hover"
                                sx={{ fontSize: 13, fontWeight: 600 }}
                              >
                                {row.taskCode}
                              </Link>
                            ) : (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>{row.clientName || '-'}</TableCell>
                          <TableCell>{row.isActive ? 'Active' : 'Paused'}</TableCell>
                          <TableCell align="right"><DollarFormatter value={row.spend} /></TableCell>
                          <TableCell align="right">{row.impressions.toLocaleString()}</TableCell>
                          <TableCell align="right">
                            {row.videoPlays3s > 0 ? <PercentageFormatter value={row.hookRate} /> : '-'}
                            <ComparisonNote comp={comparisons.hookRate} compact />
                          </TableCell>
                          <TableCell align="right">
                            <PercentageFormatter value={row.ctr} />
                            <ComparisonNote comp={comparisons.ctr} compact />
                          </TableCell>
                          <TableCell align="right">
                            {row.clicks > 0 ? <PercentageFormatter value={row.lpvRate} /> : '-'}
                            <ComparisonNote comp={comparisons.lpvRate} compact />
                          </TableCell>
                          <TableCell align="right">
                            {row.videoPlays3s > 0 ? <PercentageFormatter value={row.thruplayRate} /> : '-'}
                            <ComparisonNote comp={comparisons.thruplayRate} compact />
                          </TableCell>
                          <TableCell align="right">{row.leads.toLocaleString()}</TableCell>
                          <TableCell align="right">
                            {row.cpl > 0 ? <DollarFormatter value={row.cpl} /> : '-'}
                            <ComparisonNote comp={comparisons.cpl} compact />
                          </TableCell>
                          <TableCell align="right">{row.booked.toLocaleString()}</TableCell>
                          <TableCell align="right">{row.demos.toLocaleString()}</TableCell>
                          <TableCell align="right">{row.netSold.toLocaleString()}</TableCell>
                          <TableCell align="right">{row.netAmount > 0 ? <DollarFormatter value={row.netAmount} /> : '-'}</TableCell>
                          <TableCell align="right">
                            {row.netAmount > 0
                              ? <StatusPill label={formatPercentLabel(row.costOfMarketing)} tone={getRatioTone(row.costOfMarketing)} />
                              : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {row.predictedCostOfMarketing != null
                              ? <StatusPill label={formatPercentLabel(row.predictedCostOfMarketing)} tone={getRatioTone(row.predictedCostOfMarketing)} />
                              : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {row.effectiveCostOfMarketing != null
                              ? <StatusPill label={formatPercentLabel(row.effectiveCostOfMarketing)} tone={getRatioTone(row.effectiveCostOfMarketing)} />
                              : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {row.spendShare > 0
                              ? <StatusPill label={formatPercentLabel(row.spendShare)} tone={getRatioTone(row.spendShare)} />
                              : '-'}
                          </TableCell>
                          <TableCell>
                            <StatusPill label={row.overallRecommendation || '-'} tone={getRecommendationTone(row.overallRecommendation)} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <DesignerPreviewDialog row={previewRow} onClose={() => setPreviewRow(null)} />
      </Stack>
    </Page>
  );
}
